import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import useValidateInput, { ValidateOption } from "hooks/useValidateInput";
import { forwardRef, useImperativeHandle } from "react";
import { SELECTED_DEFAULT } from "constants/format";
import { TiresizeAspectRatio, TiresizeRimDiam, TiresizeWidth } from "../types/tire";

interface CarTiresizeInputProps {
  label: string;
  width?: string;
  value?: string;
  list?: TiresizeWidth[] | TiresizeAspectRatio[] | TiresizeRimDiam[];
  handleChange: (event: SelectChangeEvent) => void;
  validateOption?: ValidateOption;
  onError?: (error: boolean, message: string) => void;
}

export interface CarTiresizeInputRef {
  validate: () => boolean;
}

/**
 * CarTiresizeInput is a React functional component that renders a labeled input field for car tire sizes.
 * @param {object} props - The properties object.
 * @param {string} props.label - The label text to display above the input field.
 * @param {string} [props.width] - The width of the input field container. Defaults to "auto" if not provided.
 * @param {string} props.value - The current value of the input field.
 * @param {Function} props.onChange - The callback function to handle changes to the input field value.
 * @returns {Element} The rendered component.
 */
const CarTiresizeInput = forwardRef(
  (
    { label, width, value, handleChange, list, validateOption, onError }: CarTiresizeInputProps,
    ref,
  ) => {
    const {
      state: { error },
      handleChangeText,
    } = useValidateInput({
      ...validateOption,
      onError,
    });

    const handleInputChange = (event: SelectChangeEvent) => {
      handleChange(event);
      handleChangeText(event.target.value);
    };

    useImperativeHandle(ref, () => ({
      validate() {
        return handleChangeText(String(value));
      },
    }));

    return (
      <Box
        sx={{
          width: width || "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ fontSize: { xs: 20, md: 23 }, fontWeight: "medium", color: "var(--black)" }}>{label}</Box>
        <FormControl fullWidth>
          <Select
            labelId="tire-size-select-label"
            id="tire-size-select"
            value={value}
            onChange={handleInputChange}
            sx={{
              border: error ? ".5px solid var(--red-800)" : ".5px solid var(--gray-200)",
              borderRadius: "9px",
              "& .MuiOutlinedInput-input": {
                padding: "13.5px 14px",
              },
            }}
          >
            <MenuItem value={SELECTED_DEFAULT} style={{ display: "none" }} disabled />
            {list?.map((item) => {
              const itemValue =
                "tire_width" in item
                  ? item.tire_width
                  : "tire_aspect_ratio" in item
                    ? item.tire_aspect_ratio
                    : item.tire_rim_diam;
              return (
                <MenuItem key={itemValue} value={Number(itemValue)}>
                  {itemValue}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    );
  },
);

export default CarTiresizeInput;
